import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Upload, Send, ShieldCheck, Play, Pause, Search,
  Users, Clock, CheckCircle2, XCircle, Ban, Inbox, Mail, SkipForward,
} from "lucide-react";

const STATUS_STYLE = {
  pending: "text-amber-700 dark:text-amber-400",
  sent: "text-emerald-700 dark:text-emerald-400",
  failed: "text-rose-700 dark:text-rose-400",
  unsubscribed: "text-paw-text-muted dark:text-white/30",
  skipped: "text-sky-700 dark:text-sky-400",
};

export default function AdminOutreach() {
  const [stats, setStats] = useState({
    total: 0, pending: 0, sent: 0, failed: 0, unsubscribed: 0, skipped: 0,
    test_approved: false, campaign_running: false, current_type: null,
    sent_today: 0, daily_cap: 80,
  });
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [audience, setAudience] = useState("all");

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const pollRef = useRef(null);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/outreach/stats");
      setStats(data);
      return data;
    } catch (e) {
      // stats poll failures are non-fatal
    }
  }, []);

  const loadContacts = useCallback(async () => {
    try {
      const { data } = await api.get("/outreach/contacts", {
        params: { type: typeFilter, status: statusFilter, search, page, page_size: pageSize },
      });
      setContacts(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, search, page]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadStats(), loadContacts()]);
    setLoading(false);
  }, [loadStats, loadContacts]);

  useEffect(() => { loadContacts(); }, [loadContacts]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // Live progress: poll while a campaign is running.
  useEffect(() => {
    if (stats.campaign_running) {
      if (!pollRef.current) {
        pollRef.current = setInterval(async () => {
          const s = await loadStats();
          await loadContacts();
          if (s && !s.campaign_running) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }, 5000);
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [stats.campaign_running, loadStats, loadContacts]);

  const act = async (key, fn) => {
    setBusy(key);
    try { await fn(); }
    finally { setBusy(""); }
  };

  const doImport = () => act("import", async () => {
    try {
      const { data } = await api.post("/outreach/import");
      toast.success(data.message || "Imported");
      await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  });

  const doTest = () => act("test", async () => {
    try {
      const { data } = await api.post("/outreach/test");
      toast.success(data.message || "Test email sent");
    } catch (e) { toast.error(formatApiError(e)); }
  });

  const toggleApprove = () => act("approve", async () => {
    try {
      const endpoint = stats.test_approved ? "/outreach/revoke-test" : "/outreach/approve-test";
      const { data } = await api.post(endpoint);
      toast.success(data.message);
      await loadStats();
    } catch (e) { toast.error(formatApiError(e)); }
  });

  const startCampaign = () => act("start", async () => {
    try {
      const { data } = await api.post("/outreach/start", { type: audience });
      toast.success(data.message);
      await loadStats();
    } catch (e) { toast.error(formatApiError(e)); }
  });

  const pauseCampaign = () => act("pause", async () => {
    try {
      const { data } = await api.post("/outreach/pause");
      toast.success(data.message);
      await loadStats();
    } catch (e) { toast.error(formatApiError(e)); }
  });

  const cards = [
    { label: "Total", value: stats.total, icon: Users, color: "" },
    { label: "Pending", value: stats.pending, icon: Clock, color: "text-amber-700 dark:text-amber-400" },
    { label: "Sent", value: stats.sent, icon: CheckCircle2, color: "text-emerald-700 dark:text-emerald-400" },
    { label: "Skipped", value: stats.skipped, icon: SkipForward, color: "text-sky-700 dark:text-sky-400" },
    { label: "Failed", value: stats.failed, icon: XCircle, color: "text-rose-700 dark:text-rose-400" },
    { label: "Unsubscribed", value: stats.unsubscribed, icon: Ban, color: "text-paw-text-muted dark:text-white/40" },
  ];

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-6xl" data-testid="admin-outreach">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Growth</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Partner Outreach</h1>
          <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mt-2">Personalized partnership emails to pet insurers and charities. Test first, approve, then send throttled.</p>
        </div>
        <button onClick={loadAll} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 font-manrope text-sm transition-colors" data-testid="outreach-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {cards.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-xs mb-2"><Icon className="w-4 h-4" /> {label}</div>
            <p className={`font-outfit font-bold text-3xl ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Campaign controls */}
      <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-5 sm:p-6 mb-8">
        <h2 className="font-outfit font-bold text-lg mb-1 flex items-center gap-2"><Mail className="w-4 h-4 text-paw-accent" /> Campaign</h2>
        <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mb-5">Sends one email every 20 to 40 seconds with a daily cap of {stats.daily_cap}. Each email carries the PawLinx logo and a working unsubscribe link. Every org stays in the list, but each inbox is emailed only once: orgs that share an address with one already contacted are marked Skipped, and anyone unsubscribed is suppressed.</p>

        {/* Step 1: import + test */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <button onClick={doImport} disabled={busy === "import"} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 disabled:opacity-60 font-manrope text-sm transition-colors" data-testid="outreach-import">
            {busy === "import" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import contacts
          </button>
          <button onClick={doTest} disabled={busy === "test"} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 disabled:opacity-60 font-manrope text-sm transition-colors" data-testid="outreach-test">
            {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test email
          </button>
        </div>

        {/* Step 2: approval gate */}
        <div className="flex flex-wrap items-center gap-3 mb-5 p-4 rounded-xl bg-black/[0.02] dark:bg-white/[0.03] border border-paw-border dark:border-white/5">
          <ShieldCheck className={`w-5 h-5 ${stats.test_approved ? "text-emerald-700 dark:text-emerald-400" : "text-paw-text-muted dark:text-white/30"}`} />
          <div className="flex-1 min-w-[180px]">
            <p className="font-manrope text-sm">{stats.test_approved ? "Test approved. Campaign enabled" : "Test not yet approved"}</p>
            <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Check the test inbox first, then approve to unlock sending.</p>
          </div>
          <button onClick={toggleApprove} disabled={busy === "approve"} className={`flex items-center gap-2 px-4 h-10 rounded-lg border disabled:opacity-60 font-manrope text-sm font-semibold transition-colors ${stats.test_approved ? "bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border-paw-border dark:border-white/10" : "bg-emerald-500/90 hover:bg-emerald-500 border-emerald-400/30 text-white"}`} data-testid="outreach-approve">
            {busy === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {stats.test_approved ? "Revoke approval" : "Approve test & enable campaign"}
          </button>
        </div>

        {/* Step 3: start / pause */}
        <div className="flex flex-wrap items-center gap-3">
          <select value={audience} onChange={(e) => setAudience(e.target.value)} disabled={stats.campaign_running} className="h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 font-manrope text-sm focus:outline-none focus:border-paw-accent text-paw-text dark:text-white disabled:opacity-60" data-testid="outreach-audience">
            <option value="all">All audiences</option>
            <option value="insurer">Insurers only</option>
            <option value="charity">Charities only</option>
          </select>
          <button onClick={startCampaign} disabled={!stats.test_approved || stats.campaign_running || busy === "start"} className="flex items-center gap-2 px-5 h-10 rounded-lg bg-paw-accent hover:bg-paw-accent-hover disabled:opacity-60 text-white font-manrope text-sm font-semibold transition-colors" data-testid="outreach-start">
            {busy === "start" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Start campaign
          </button>
          <button onClick={pauseCampaign} disabled={!stats.campaign_running || busy === "pause"} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 disabled:opacity-60 font-manrope text-sm transition-colors" data-testid="outreach-pause">
            {busy === "pause" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4" />} Pause
          </button>
          {stats.campaign_running && (
            <span className="flex items-center gap-2 font-manrope text-sm text-emerald-700 dark:text-emerald-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Running ({stats.current_type}) · {stats.sent_today}/{stats.daily_cap} today
            </span>
          )}
        </div>
      </div>

      {/* Contacts table */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h2 className="font-outfit font-bold text-lg mr-auto">Contacts</h2>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-paw-text-muted dark:text-white/30" />
          <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Search name or email" className="h-10 pl-9 pr-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 font-manrope text-sm focus:outline-none focus:border-paw-accent text-paw-text dark:text-white placeholder:text-paw-text-muted dark:placeholder:text-white/30 w-56" data-testid="outreach-search" />
        </div>
        <select value={typeFilter} onChange={(e) => { setPage(1); setTypeFilter(e.target.value); }} className="h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 font-manrope text-sm focus:outline-none focus:border-paw-accent text-paw-text dark:text-white" data-testid="outreach-filter-type">
          <option value="">All types</option>
          <option value="insurer">Insurers</option>
          <option value="charity">Charities</option>
        </select>
        <select value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }} className="h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 font-manrope text-sm focus:outline-none focus:border-paw-accent text-paw-text dark:text-white" data-testid="outreach-filter-status">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="sent">Sent</option>
          <option value="skipped">Skipped</option>
          <option value="failed">Failed</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : contacts.length === 0 ? (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-12 text-center font-manrope text-paw-text-muted dark:text-white/40">
          <Inbox className="w-8 h-8 mx-auto mb-3 text-paw-text-muted dark:text-white/20" strokeWidth={1.5} />
          No contacts. Click “Import contacts” to seed the list from the CSVs.
        </div>
      ) : (
        <>
          <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left font-manrope text-sm">
                <thead>
                  <tr className="text-paw-text-muted dark:text-white/40 text-xs uppercase tracking-wider border-b border-paw-border dark:border-white/5">
                    <th className="px-5 py-3 font-medium">Organization</th>
                    <th className="px-5 py-3 font-medium">Type</th>
                    <th className="px-5 py-3 font-medium">Email</th>
                    <th className="px-5 py-3 font-medium">City</th>
                    <th className="px-5 py-3 font-medium">State</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id} className="border-b border-paw-border dark:border-white/5 last:border-0">
                      <td className="px-5 py-3 text-paw-text dark:text-white/90">{c.name}</td>
                      <td className="px-5 py-3 text-paw-text-muted dark:text-white/50 capitalize">{c.type}</td>
                      <td className="px-5 py-3 text-paw-text-secondary dark:text-white/60">{c.email}</td>
                      <td className="px-5 py-3 text-paw-text-muted dark:text-white/50">{c.city}</td>
                      <td className="px-5 py-3 text-paw-text-muted dark:text-white/50">{c.state}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs capitalize ${STATUS_STYLE[c.status] || "text-paw-text-muted dark:text-white/50"}`} title={c.error || ""}>{c.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex items-center justify-between mt-4 font-manrope text-sm text-paw-text-muted dark:text-white/40">
            <span>{total} contact{total === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 h-9 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 disabled:opacity-60 transition-colors">Prev</button>
              <span>Page {page} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 h-9 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 disabled:opacity-60 transition-colors">Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
